import { useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  RadioButton,
  Button,
  Modal,
  Dialog,
  Text,
  Row,
  Column,
} from '@oliasoft-open-source/react-ui-library';

import {
  HierarchyLevelType, SecurityClassification,
} from 'common/interfaces/hierarchy-entities';
import translations from 'client/internationalization/translation-map.json';
import { updateNodeClassificationLevel } from 'client/store/entities/hierarchy/node/node';
import { updateObjectClassificationLevel } from 'client/store/entities/object/object';
import { TSelectedItem } from 'client/views/explore/hierarchy/hierarchy.interfaces';

interface IClassificationLevelControlModalProps {
  onCloseClassificationLevelControlModal: () => void;
  showModal: boolean;
  selectedItem?: TSelectedItem;
  updateNodeClassificationLevel: Function;
  updateObjectClassificationLevel: Function;
}

const ClassificationLevelControlModal = ({
  onCloseClassificationLevelControlModal,
  showModal,
  selectedItem,
  updateNodeClassificationLevel,
  updateObjectClassificationLevel,
}: IClassificationLevelControlModalProps) => {
  const { t } = useTranslation();
  const [classificationlevel, setClassificationlevel] = useState<SecurityClassification | undefined>(selectedItem?.classificationlevel);

  const updateNodeClassification = (node: TSelectedItem) => {
    const { nodeid: id } = node;
    updateNodeClassificationLevel({
      id,
      classificationlevel,
    });
  };

  const updateObjectClassification = (object: TSelectedItem) => {
    const { objectid, nodeid } = object;
    updateObjectClassificationLevel({
      objectid,
      nodeid,
      classificationlevel,
    });
  };

  const updateClassificationLevel = (item: TSelectedItem) => {
    if (!classificationlevel) {
      onCloseClassificationLevelControlModal();
      return;
    }
    if (item.entityType === HierarchyLevelType.Node) {
      updateNodeClassification(item);
    }
    if (item.entityType === HierarchyLevelType.Object) {
      updateObjectClassification(item);
    }

    onCloseClassificationLevelControlModal();
  };

  const radioButtonsClassificationLevel = [
    {
      name: 'classificationLevel',
      value: SecurityClassification.OPEN,
      label: t(translations.classificationLevel_open),
    },
    {
      name: 'classificationLevel',
      value: SecurityClassification.INTERNAL,
      label: t(translations.classificationLevel_internal),
    },
    {
      name: 'classificationLevel',
      value: SecurityClassification.RESTRICTED,
      label: t(translations.classificationLevel_restricted),
    },
    {
      name: 'classificationLevel',
      value: SecurityClassification.CONFIDENTIAL,
      label: t(translations.classificationLevel_confidential),
    },
  ];

  const selectedClassificationLevelOption = radioButtonsClassificationLevel.find(radioOption => radioOption.value as string === classificationlevel) || '';

  const classificationLevelControl = (
    <>
      <Row marginBottom={10}>
        <Column>
          <Text bold>{t(translations.classificationLevel)}</Text>
        </Column>
      </Row>
      <Row>
        <Column>
          <RadioButton
            options={radioButtonsClassificationLevel}
            value={selectedClassificationLevelOption}
            onChange={(evt) => setClassificationlevel(evt.target.value as SecurityClassification)}
            inline
          />
        </Column>
      </Row>
    </>
  );

  return (
    <Modal visible={showModal} centered onEscape={onCloseClassificationLevelControlModal}>
      <Dialog
        dialog={{
          heading: selectedItem?.name,
          width: 400,
          contentPadding: 'var(--padding)',
          content: classificationLevelControl,
          footer: (
            <div>
              <Button
                label={t(translations.update)}
                onClick={() => updateClassificationLevel(selectedItem!)}
                colored
                disabled={!selectedItem}
              />
              <Button label={t(translations.cancel)} onClick={onCloseClassificationLevelControlModal} />
            </div>
          ),
          onClose: onCloseClassificationLevelControlModal,
        }}
      />
    </Modal>
  );
};

const mapDispatchToProps = {
  updateNodeClassificationLevel,
  updateObjectClassificationLevel,
};

const Container = connect(null, mapDispatchToProps)(ClassificationLevelControlModal);

export { Container as ClassificationLevelControlModal };
